import { useEffect, useState } from 'react';
import { Loader2, Save, Sparkles, X } from 'lucide-react';
import type { TrinityCardSlot, TrinityCardsTask } from '../../services/directorApi';

interface Props {
  open: boolean;
  slot: TrinityCardSlot;
  cardLabel: string;
  hasExistingPrompt: boolean;
  existingPrompt: string | null;
  aiCost: number | null;
  aiEnabled: boolean;
  task: TrinityCardsTask | null;
  onConfirm: () => void;
  onSave: (prompt: string) => void;
  onClose: () => void;
}

export function TrinityPromptGenerateDialog({
  open,
  slot,
  cardLabel,
  hasExistingPrompt,
  existingPrompt,
  aiCost,
  aiEnabled,
  task,
  onConfirm,
  onSave,
  onClose,
}: Props) {
  const [prompt, setPrompt] = useState('');
  const [step, setStep] = useState<'confirm' | 'generating' | 'editing'>('confirm');

  useEffect(() => {
    const running = task?.status === 'pending' || task?.status === 'running';
    const sameSlot = task?.targetSlot === slot;
    if (running && sameSlot) {
      setStep('generating');
    } else if (task?.status === 'completed' && sameSlot) {
      setStep('editing');
    } else if (!task && hasExistingPrompt && step === 'confirm') {
      // 任务已清理但文案已存在（弹窗关闭后重开），直接进编辑态
      setStep('editing');
    } else if (!task && step === 'confirm') {
      // 仅初始状态才回到确认页，防止从 generating/editing 回退
      setStep('confirm');
    }
    // 当 task 变 null 且 step 已在 generating/editing 时，不做任何操作
  }, [task, slot, open, hasExistingPrompt, step]);

  // 编辑态下，卡片数据异步刷新后同步最新文案
  useEffect(() => {
    if (step === 'editing' && existingPrompt) {
      setPrompt((prev) => {
        // 如果用户还没手动编辑过（还是 draft/streaming 或空），用最新卡片数据
        const draft = task?.draftPrompts?.[slot];
        if (draft) return draft;
        return prev || existingPrompt;
      });
    }
  }, [existingPrompt, step, slot, task]);

  // 弹窗关闭时重置状态
  useEffect(() => {
    if (!open) {
      setPrompt('');
      setStep('confirm');
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">
            {step === 'confirm'
              ? `生成${cardLabel}文案`
              : step === 'generating'
                ? `正在生成${cardLabel}文案`
                : `${cardLabel}文案`}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4">
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                <p>
                  即将调用 AI 生成 <strong>{cardLabel}</strong> 的提示词文案。
                </p>
                {typeof aiCost === 'number' && (
                  <p className="mt-2 font-medium text-violet-700">
                    预计消耗 AI 融合积分：{aiCost} 积分
                  </p>
                )}
                {hasExistingPrompt && (
                  <p className="mt-2 text-amber-600">
                    注意：将覆盖现有文案
                  </p>
                )}
                {!aiEnabled && (
                  <p className="mt-2 text-rose-600">
                    当前 AI 融合未开放，请联系管理员
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    setStep('generating');
                    onConfirm();
                  }}
                  disabled={!aiEnabled}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  确认生成
                </button>
              </div>
            </div>
          )}

          {step === 'generating' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-violet-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm font-medium">AI 正在生成提示词…</span>
              </div>
              <div className="min-h-[8rem] rounded-xl bg-slate-50 p-3">
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {task?.streamingPartial?.[slot] || ''}
                  <span className="inline-block h-4 w-0.5 animate-pulse bg-violet-400 align-middle" />
                </p>
              </div>
              <p className="text-xs text-slate-400">
                关闭弹窗不会中断生成，您可以稍后回来查看结果。
              </p>
            </div>
          )}

          {step === 'editing' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                生成完成，您可以修改后保存：
              </p>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[8rem] w-full rounded-xl border border-slate-200 bg-white p-3 text-sm leading-6 text-slate-700 outline-none transition focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                placeholder="输入提示词..."
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    onSave(prompt);
                    onClose();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-500"
                >
                  <Save className="h-4 w-4" />
                  保存
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
